import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, MapPin, Tv, ArrowRight, Users } from "lucide-react";

const heroImg = "https://images.pexels.com/photos/14391561/pexels-photo-14391561.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const canadaImg = "https://images.unsplash.com/photo-1638848424383-c59f174814ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjYW5hZGlhbiUyMGxhbmRzY2FwZSUyMG1vdW50YWlucyUyMGxha2UlMjBiYW5mZnxlbnwwfHx8fDE3ODQ2NjYxODN8MA&ixlib=rb-4.1.0&q=85";
const profileImgs = [
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600",
  "https://images.unsplash.com/photo-1631884884183-84a8d1f9220b?w=600",
  "https://images.pexels.com/photos/9038658/pexels-photo-9038658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10 px-6 md:px-10 pt-12 md:pt-20 pb-16">
          <div className="md:col-span-7 flex flex-col justify-center fade-up">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
              <Sparkles className="w-3 h-3" /> Casting now for Season 1 in the GTHA
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-foreground">
              Finding love,
              <br />
              from <span className="italic text-primary">Hamilton</span> to
              <br />
              <span className="italic">Oakville.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              A warm little corner of the internet for singles across Hamilton, Burlington, Oakville, Mississauga, Toronto and everywhere in between — and maybe, just maybe, our new dating series.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button
                  data-testid="hero-signup-btn"
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-7 h-12 shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                >
                  Create your profile
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/apply">
                <Button
                  data-testid="hero-apply-btn"
                  size="lg"
                  variant="outline"
                  className="rounded-full h-12 px-7 border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Tv className="w-4 h-4 mr-1" /> Apply to the show
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex -space-x-2">
                {profileImgs.map((src, i) => (
                  <img key={i} src={src} className="w-8 h-8 rounded-full border-2 border-background object-cover" alt="" />
                ))}
              </div>
              <div>Join <span className="text-foreground font-semibold">2,300+</span> singles across the Greater Toronto & Hamilton Area</div>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={heroImg} alt="Couple at sunset" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-card border border-border rounded-2xl p-4 shadow-xl backdrop-blur-xl w-56">
              <div className="flex items-center gap-2 text-primary">
                <Heart className="w-4 h-4 fill-primary" />
                <span className="font-serif text-lg">147 matches</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">made in Ontario this week</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8 relative rounded-3xl overflow-hidden bg-accent text-accent-foreground p-10 md:p-14 min-h-[360px]">
            <img src={canadaImg} alt="Canadian mountains" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs mb-6">
                <Tv className="w-3 h-3" /> The Show
              </div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight max-w-md">Could your love story be next on TV?</h2>
              <p className="mt-4 text-white/85 max-w-md leading-relaxed">
                We're casting singles across the Greater Toronto & Hamilton Area — from downtown Hamilton to the Toronto waterfront, and every neighbourhood in between.
              </p>
              <Link to="/apply">
                <Button data-testid="feature-apply-btn" size="lg" className="mt-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  Apply now <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 grid gap-6">
            <div className="rounded-3xl bg-secondary p-8 flex flex-col justify-between min-h-[170px]">
              <Users className="w-6 h-6 text-primary" />
              <div>
                <div className="font-serif text-2xl leading-tight">Join local singles<br/>across Ontario</div>
              </div>
            </div>
            <div className="rounded-3xl bg-primary text-primary-foreground p-8 flex flex-col justify-between min-h-[170px]">
              <MapPin className="w-6 h-6" />
              <div>
                <div className="font-serif text-3xl">GTHA</div>
                <div className="text-sm text-primary-foreground/85">Hamilton · Toronto · Mississauga · Burlington & more</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-2xl mb-14">
          <div className="text-primary text-sm font-medium mb-3">How it works</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">Three warm steps to find someone worth staying home for.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Build your profile", d: "Add a title, a description, and photos that actually feel like you.", img: profileImgs[0] },
            { n: "02", t: "Tell your story", d: "Share what makes you, you — from Sunday hikes to the way you order coffee.", img: profileImgs[1] },
            { n: "03", t: "Apply to the show", d: "Ready for TV? Submit your application and let our casting team find you the one.", img: profileImgs[2] },
          ].map((s, i) => (
            <div key={s.n} className="fade-up group" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="aspect-[4/5] overflow-hidden rounded-2xl mb-5">
                <img src={s.img} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="text-primary text-sm font-mono">{s.n}</div>
              <div className="font-serif text-2xl mt-1">{s.t}</div>
              <div className="text-muted-foreground mt-2 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
